import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { isEqual } from 'lodash';
import removeTypename from 'commons/removeTypename';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import useIsRefreshCache from 'hooks/useIsRefreshCache';
import addDerivedProductData from './helpers/addDerivedProductData';

// This hook provides a reusable way to load all products for a product listing
// page, such as the category page. It first loads just initial product data
// (including filters), then when the browser is idle it loads all products.

// Uses useQuery (not useSuspenseQuery) so the query runs client-side with auth.
// This ensures trade users see correct prices on category pages (SSR would run
// without auth and return regular prices).

// To enable this functionality, it requires that the specified GraphQL query
// supports a limit variable which allows -1 to be passed in to load all.

const useProductListingQuery = (query, t0) => {
  const $ = _c(38);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let otherOptions;
  let serverData;
  let t2;
  let t3;
  let t4;
  let variables;
  if ($[2] !== options) {
    ({
      variables,
      limit: t2,
      skip: t3,
      serverSidePaginationEnabled: t4,
      serverData,
      ...otherOptions
    } = options);
    $[2] = options;
    $[3] = otherOptions;
    $[4] = serverData;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = variables;
  } else {
    otherOptions = $[3];
    serverData = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    variables = $[8];
  }
  const limit = t2 === undefined ? NUMBER_OF_PRODUCTS_TO_SHOW : t2;
  const skipQuery = t3 === undefined ? false : t3;
  const serverSidePaginationEnabled = t4 === undefined ? false : t4;
  const hasLoadingAllDataStartedRef = useRef(false);
  let t5;
  if ($[9] !== variables) {
    t5 = {
      ...variables
    };
    $[9] = variables;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const prevVariables = useRef(t5);
  const [allData, setAllData] = useState(null);
  const isRefreshCache = useIsRefreshCache();
  const isAllDataInInitialRequest = limit === -1;
  const queryName = query?.definitions?.[0]?.selectionSet?.selections?.[0]?.alias?.value ?? query?.definitions?.[0]?.name?.value;
  const skip = skipQuery || !queryName;
  const skipAllDataLoading = skip || serverSidePaginationEnabled;
  let t6;
  if ($[11] !== isRefreshCache || $[12] !== limit || $[13] !== variables) {
    t6 = {
      ...variables,
      limit,
      refreshCache: isRefreshCache
    };
    $[11] = isRefreshCache;
    $[12] = limit;
    $[13] = variables;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== otherOptions || $[16] !== skip || $[17] !== t6) {
    t7 = {
      fetchPolicy: "cache-and-network",
      variables: t6,
      skip,
      ...otherOptions
    };
    $[15] = otherOptions;
    $[16] = skip;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    data: initialData,
    loading
  } = useQuery(query, t7);
  const effectiveInitialData = initialData ?? serverData;
  const isInitialDataLoading = loading && !effectiveInitialData;
  const client = useApolloClient();
  let t8;
  let t9;
  if ($[19] !== client || $[20] !== isAllDataInInitialRequest || $[21] !== isRefreshCache || $[22] !== otherOptions || $[23] !== query || $[24] !== skipAllDataLoading || $[25] !== variables) {
    t8 = () => {
      if (isAllDataInInitialRequest || skipAllDataLoading) {
        return;
      }
      if (!hasLoadingAllDataStartedRef.current || !isEqual(prevVariables.current, variables)) {
        hasLoadingAllDataStartedRef.current = true;
        prevVariables.current = {
          ...variables
        };
        window.requestIdleCallback(async () => {
          const {
            data: updatedAllData
          } = await client.query({
            query,
            variables: {
              ...variables,
              limit: -1,
              refreshCache: isRefreshCache
            },
            skip: skipAllDataLoading,
            ...otherOptions
          });
          setAllData(updatedAllData);
        });
      }
    };
    t9 = [client, isAllDataInInitialRequest, isRefreshCache, otherOptions, query, skipAllDataLoading, variables];
    $[19] = client;
    $[20] = isAllDataInInitialRequest;
    $[21] = isRefreshCache;
    $[22] = otherOptions;
    $[23] = query;
    $[24] = skipAllDataLoading;
    $[25] = variables;
    $[26] = t8;
    $[27] = t9;
  } else {
    t8 = $[26];
    t9 = $[27];
  }
  useEffect(t8, t9);
  const isAllDataLoading = !serverSidePaginationEnabled && !isAllDataInInitialRequest && !allData;
  const dataResponse = allData ?? effectiveInitialData;
  const data = dataResponse?.[queryName];
  let t10;
  if ($[28] !== data) {
    t10 = data ? removeTypename(data) : {};
    $[28] = data;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const pageData = t10;
  const {
    products: productsWithoutDerivedData,
    upholsteryOptions
  } = pageData;
  let t11;
  if ($[30] !== productsWithoutDerivedData || $[31] !== upholsteryOptions) {
    t11 = productsWithoutDerivedData?.map(product => addDerivedProductData({
      product,
      upholsteryOptions
    })) ?? [];
    $[30] = productsWithoutDerivedData;
    $[31] = upholsteryOptions;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const products = t11;
  let t12;
  if ($[33] !== isAllDataLoading || $[34] !== isInitialDataLoading || $[35] !== pageData || $[36] !== products) {
    t12 = {
      ...pageData,
      isAllDataLoading,
      isInitialDataLoading,
      products
    };
    $[33] = isAllDataLoading;
    $[34] = isInitialDataLoading;
    $[35] = pageData;
    $[36] = products;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
export default useProductListingQuery;